import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapClickHandler, MapViewController } from './MapController';
import DraggableMarker from './DraggableMarker';
import HighlightablePolyline from './HighlightablePolyline';
import ResultsPanel from './ResultsPanel';
import { searchLocation } from '../utils/mapHelpers';
import { calculateRouteGeometry, analyzeStandardRoute, analyzeAlternativeRoute } from '../utils/routing';
import { DEFAULT_SURFACE_PREFERENCES, SURFACE_ICONS, SURFACE_COLORS } from '../config/constants';

// Configuration des icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function GravelRouteTracker({ currentUser, onLogout }) {
    // États principaux
    const [points, setPoints] = useState([]);
    const [routeGeometry, setRouteGeometry] = useState([]);
    const [alternativeRouteGeometry, setAlternativeRouteGeometry] = useState([]);
    const [routeData, setRouteData] = useState(null);
    const [alternativeRouteData, setAlternativeRouteData] = useState(null);
    const [routeSegments, setRouteSegments] = useState([]);
    const [alternativeRouteSegments, setAlternativeRouteSegments] = useState([]);

    // États de l'interface
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoRouting] = useState(true);
    const [isCalculating, setIsCalculating] = useState(false);
    const [mapLayer, setMapLayer] = useState('satellite');
    const [showLeftPanel, setShowLeftPanel] = useState(true);
    const [showRightPanel, setShowRightPanel] = useState(false);
    const [mapView, setMapView] = useState(null);
    const [transportMode, setTransportMode] = useState('bike');

    // États de recherche de lieu
    const [startLocation, setStartLocation] = useState('');
    const [endLocation, setEndLocation] = useState('');
    const [startSuggestions, setStartSuggestions] = useState([]);
    const [endSuggestions, setEndSuggestions] = useState([]);

    // États des préférences de surface
    const [useSurfacePreference, setUseSurfacePreference] = useState(false);
    const [surfacePreferences, setSurfacePreferences] = useState(DEFAULT_SURFACE_PREFERENCES);
    const [tempSurfacePreferences, setTempSurfacePreferences] = useState(DEFAULT_SURFACE_PREFERENCES);

    // État pour contrôler la visibilité des tracés
    const [showStandardRoute, setShowStandardRoute] = useState(true);
    const [showAlternativeRoute, setShowAlternativeRoute] = useState(true);

    // État pour le survol des tracés
    const [isStandardRouteHovered, setIsStandardRouteHovered] = useState(false);
    const [isAlternativeRouteHovered, setIsAlternativeRouteHovered] = useState(false);

    // État pour le debounce du drag
    const [dragTimeout, setDragTimeout] = useState(null);

    // Recalculer l'itinéraire quand le mode de transport change
    useEffect(() => {
        if (points.length >= 2 && autoRouting && !isCalculating) {
            const timer = setTimeout(() => {
                handleCalculateRoute(points);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [transportMode]);

    // Ajouter un point sur la carte
    const addPoint = async (point) => {
        const newPoints = [...points, point];
        setPoints(newPoints);
        if (autoRouting && newPoints.length >= 2) {
            await handleCalculateRoute(newPoints);
        }
    };

    // Gérer le déplacement d'un marqueur
    const handleMarkerDrag = async (index, newPosition) => {
        const newPoints = [...points];
        newPoints[index] = newPosition;
        setPoints(newPoints);

        // Annuler le timeout précédent
        if (dragTimeout) {
            clearTimeout(dragTimeout);
        }

        // Attendre 1 seconde avant de recalculer (évite trop de requêtes)
        const timeout = setTimeout(() => {
            if (autoRouting && newPoints.length >= 2) {
                handleCalculateRoute(newPoints);
            }
        }, 1000);

        setDragTimeout(timeout);
    };

    // Calculer l'itinéraire
    const handleCalculateRoute = async (pointsList) => {
        if (isCalculating) return;
        setIsCalculating(true);
        setError(null); // Effacer les erreurs précédentes

        try {
            const geometry = await calculateRouteGeometry(pointsList, transportMode);
            if (geometry) {
                setRouteGeometry(geometry);
            }
        } catch (err) {
            console.error('Erreur calcul itinéraire:', err);
            // Ne pas afficher l'erreur si c'est juste un rate limit temporaire
            if (!err.message?.includes('rate') && !err.message?.includes('429')) {
                setError('Erreur de calcul. Réessaye dans quelques secondes.');
            }
        } finally {
            setIsCalculating(false);
        }
    };

    // Rechercher un lieu
    const handleSearchLocation = async (query, isStart) => {
        const results = await searchLocation(query);
        if (isStart) {
            setStartSuggestions(results);
        } else {
            setEndSuggestions(results);
        }
    };

    // Sélectionner une suggestion de lieu
    const selectSuggestion = async (suggestion, isStart) => {
        const coords = [parseFloat(suggestion.lat), parseFloat(suggestion.lon)];

        if (isStart) {
            setStartLocation(suggestion.display_name);
            setStartSuggestions([]);

            if (points.length === 0) {
                setPoints([coords]);
                setMapView({ type: 'center', center: coords, zoom: 14, key: Date.now() });
            } else {
                const newPoints = [coords, ...points.slice(1)];
                setPoints(newPoints);
                setMapView(
                    newPoints.length >= 2
                        ? { type: 'bounds', bounds: [newPoints[0], newPoints[newPoints.length - 1]], key: Date.now() }
                        : { type: 'center', center: coords, zoom: 14, key: Date.now() }
                );
                if (autoRouting && newPoints.length >= 2) {
                    await handleCalculateRoute(newPoints);
                }
            }
        } else {
            setEndLocation(suggestion.display_name);
            setEndSuggestions([]);

            if (points.length === 0) {
                setPoints([coords]);
                setMapView({ type: 'center', center: coords, zoom: 14, key: Date.now() });
            } else if (points.length === 1) {
                const newPoints = [...points, coords];
                setPoints(newPoints);
                setMapView({ type: 'bounds', bounds: [newPoints[0], newPoints[1]], key: Date.now() });
                if (autoRouting) {
                    await handleCalculateRoute(newPoints);
                }
            } else {
                const newPoints = [...points.slice(0, -1), coords];
                setPoints(newPoints);
                setMapView({ type: 'bounds', bounds: [newPoints[0], newPoints[newPoints.length - 1]], key: Date.now() });
                if (autoRouting && newPoints.length >= 2) {
                    await handleCalculateRoute(newPoints);
                }
            }
        }
    };

    // Mettre à jour les préférences de surface temporaires
    const updateTempSurfacePreference = (surface, value) => {
        setTempSurfacePreferences({
            ...tempSurfacePreferences,
            [surface]: value,
        });
    };

    // Normaliser les préférences de surface pour qu'elles totalisent 100%
    const normalizeSurfacePreferences = () => {
        const total = Object.values(tempSurfacePreferences).reduce((sum, v) => sum + v, 0);

        if (total === 0) {
            alert('Le total ne peut pas être 0%');
            return;
        }

        const normalized = {};
        Object.keys(tempSurfacePreferences).forEach((s) => {
            normalized[s] = Math.round((tempSurfacePreferences[s] / total) * 100);
        });

        const newTotal = Object.values(normalized).reduce((sum, v) => sum + v, 0);
        if (newTotal !== 100) {
            const diff = 100 - newTotal;
            const maxSurface = Object.keys(normalized).reduce((a, b) =>
                normalized[a] > normalized[b] ? a : b
            );
            normalized[maxSurface] += diff;
        }

        setSurfacePreferences(normalized);
        setTempSurfacePreferences(normalized);
    };

    // Réinitialiser les préférences de surface
    const resetSurfacePreferences = () => {
        setSurfacePreferences(DEFAULT_SURFACE_PREFERENCES);
        setTempSurfacePreferences(DEFAULT_SURFACE_PREFERENCES);
    };

    // Analyser l'itinéraire
    const analyzeRoute = async () => {
        if (points.length < 2) {
            setError('Il faut au moins 2 points pour tracer une route');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const standardResult = await analyzeStandardRoute(points, routeGeometry, transportMode);
            setRouteGeometry(standardResult.geometry);
            setRouteSegments(standardResult.segments);
            setRouteData(standardResult.data);

            if (useSurfacePreference) {
                const alternativeResult = await analyzeAlternativeRoute(
                    points,
                    transportMode,
                    surfacePreferences
                );
                setAlternativeRouteGeometry(alternativeResult.geometry);
                setAlternativeRouteSegments(alternativeResult.segments);
                setAlternativeRouteData(alternativeResult.data);
            }

            setShowRightPanel(true);
        } catch (err) {
            console.error('Erreur:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Effacer l'itinéraire

    const clearRoute = () => {
        setPoints([]);
        setRouteGeometry([]);
        setAlternativeRouteGeometry([]);
        setRouteData(null);
        setAlternativeRouteData(null);
        setRouteSegments([]);
        setAlternativeRouteSegments([]);
        setError(null);
        setStartLocation('');
        setEndLocation('');
        setStartSuggestions([]);
        setEndSuggestions([]);
        setShowRightPanel(false);
    };

    return (
        <div
            style={{
                position: 'relative',
                width: '100vw',
                height: '100vh',
                overflow: 'hidden',
                fontFamily: 'Arial, sans-serif',
            }}
        >
            {/* Carte */}
            <MapContainer
                center={[46.603354, 1.888334]}
                zoom={6}
                style={{ width: '100%', height: '100%' }}
                worldCopyJump={true}
                maxBounds={[
                    [-90, -180],
                    [90, 180],
                ]}
                maxBoundsViscosity={1.0}
            >
                {/* Couches de carte */}
                {mapLayer === 'osm' && (
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap contributors"
                    />
                )}
                {mapLayer === 'satellite' && (
                    <>
                        <TileLayer
                            url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                            attribution="&copy; Google"
                            maxZoom={20}
                        />
                        <TileLayer
                            url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
                            attribution="&copy; Google"
                            maxZoom={20}
                        />
                    </>
                )}

                <MapViewController viewKey={mapView} />
                <MapClickHandler onPointAdd={addPoint} />

                {/* Route alternative en dessous avec style différencié + survol */}
                {showAlternativeRoute && alternativeRouteSegments.length > 0 ? (
                    alternativeRouteSegments.map((seg, idx) => (
                        <HighlightablePolyline
                            key={`ar-${idx}`}
                            positions={seg.geometry}
                            color={seg.color}
                            weight={7}
                            opacity={1}
                            dashArray="10, 10"
                            isAlternative={true}
                            forceHover={isAlternativeRouteHovered}
                        />
                    ))
                ) : (
                    showAlternativeRoute && alternativeRouteGeometry.length > 1 && (
                        <HighlightablePolyline
                            positions={alternativeRouteGeometry}
                            color="#10b981"
                            weight={7}
                            opacity={1}
                            dashArray="10, 10"
                            isAlternative={true}
                            forceHover={isAlternativeRouteHovered}
                        />
                    )
                )}

                {/* Route standard par-dessus avec bordure blanche + survol */}
                {showStandardRoute && routeSegments.length > 0 ? (
                    routeSegments.map((seg, idx) => (
                        <HighlightablePolyline
                            key={`r-${idx}`}
                            positions={seg.geometry}
                            color={seg.color}
                            weight={6}
                            opacity={1}
                            showBorder={true}
                            forceHover={isStandardRouteHovered}
                        />
                    ))
                ) : (
                    showStandardRoute && routeGeometry.length > 1 && (
                        <HighlightablePolyline
                            positions={routeGeometry}
                            color="#3b82f6"
                            weight={5}
                            opacity={1}
                            showBorder={true}
                            forceHover={isStandardRouteHovered}
                        />
                    )
                )}

                {/* Marqueurs déplaçables */}
                {points.map((point, idx) => (
                    <DraggableMarker
                        key={idx}
                        position={point}
                        index={idx}
                        onDragEnd={handleMarkerDrag}
                        isStart={idx === 0}
                        isEnd={idx === points.length - 1}
                    />
                ))}
            </MapContainer>

            {/* Contrôles en haut à droite */}
            <div
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'flex-end',
                }}
            >
                {/* Bouton de déconnexion */}
                <button
                    onClick={onLogout}
                    title={`Déconnexion (${currentUser})`}
                    style={{
                        padding: '8px 14px',
                        backgroundColor: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#374151',
                    }}
                >
                    👤 {currentUser} · Déconnexion
                </button>

                {/* Bouton de changement de couche */}
                <button
                    onClick={() => setMapLayer(mapLayer === 'osm' ? 'satellite' : 'osm')}
                    style={{
                        padding: '10px 15px',
                        backgroundColor: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                    }}
                >
                    {mapLayer === 'osm' ? '🛰️' : '🗺️'}
                </button>

                {/* Contrôles de visibilité des tracés */}
                {(routeData || alternativeRouteData) && (
                    <div
                        style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            fontSize: '12px',
                            minWidth: '180px',
                        }}
                    >
                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Affichage</div>

                        {routeData && (
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '6px',
                                    cursor: 'pointer',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={showStandardRoute}
                                    onChange={(e) => setShowStandardRoute(e.target.checked)}
                                />
                                <div
                                    style={{
                                        width: '20px',
                                        height: '4px',
                                        backgroundColor: '#3b82f6',
                                        borderRadius: '2px',
                                    }}
                                />
                                <span>Route standard</span>
                            </label>
                        )}

                        {alternativeRouteData && (
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={showAlternativeRoute}
                                    onChange={(e) => setShowAlternativeRoute(e.target.checked)}
                                />
                                <div
                                    style={{
                                        width: '20px',
                                        height: '4px',
                                        backgroundColor: '#10b981',
                                        borderRadius: '2px',
                                        backgroundImage: 'repeating-linear-gradient(90deg, #10b981 0, #10b981 5px, transparent 5px, transparent 10px)',
                                    }}
                                />
                                <span>Route alternative</span>
                            </label>
                        )}
                    </div>
                )}

                {/* Légende des surfaces */}
                {(routeSegments.length > 0 || alternativeRouteSegments.length > 0) && !showRightPanel && (
                    <div
                        style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            fontSize: '11px',
                            minWidth: '140px',
                        }}
                    >
                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Légende surfaces</div>
                        {[
                            { label: 'Asphalte', color: SURFACE_COLORS.asphalt },
                            { label: 'Gravier', color: SURFACE_COLORS.gravel },
                            { label: 'Terre', color: SURFACE_COLORS.dirt },
                            { label: 'Herbe', color: SURFACE_COLORS.grass },
                            { label: 'Pavé', color: SURFACE_COLORS.paved },
                        ].map((item) => (
                            <div
                                key={item.label}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '4px',
                                }}
                            >
                                <div
                                    style={{
                                        width: '16px',
                                        height: '3px',
                                        backgroundColor: item.color,
                                        borderRadius: '2px',
                                    }}
                                />
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Panneau gauche */}
            {showLeftPanel && (
                <div
                    style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        width: '350px',
                        maxHeight: 'calc(100vh - 40px)',
                        overflow: 'auto',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        zIndex: 1000,
                    }}
                >
                    <div style={{ padding: '20px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px',
                            }}
                        >
                            <h2 style={{ margin: 0, fontSize: '20px' }}>🚴 Gravel Tracker</h2>
                            <button
                                onClick={() => setShowLeftPanel(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Champ de départ */}
                        <div style={{ marginBottom: '15px' }}>
                            <input
                                type="text"
                                value={startLocation}
                                onChange={(e) => {
                                    setStartLocation(e.target.value);
                                    handleSearchLocation(e.target.value, true);
                                }}
                                placeholder="Point de départ"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box',
                                }}
                            />
                            {startSuggestions.length > 0 && (
                                <div
                                    style={{
                                        backgroundColor: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        marginTop: '5px',
                                        maxHeight: '150px',
                                        overflow: 'auto',
                                    }}
                                >
                                    {startSuggestions.map((s, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => selectSuggestion(s, true)}
                                            style={{
                                                padding: '10px',
                                                cursor: 'pointer',
                                                borderBottom: idx < startSuggestions.length - 1 ? '1px solid #eee' : 'none',
                                                fontSize: '12px',
                                            }}
                                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                                            onMouseLeave={(e) => (e.target.style.backgroundColor = 'white')}
                                        >
                                            {s.display_name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Champ d'arrivée */}
                        <div style={{ marginBottom: '15px' }}>
                            <input
                                type="text"
                                value={endLocation}
                                onChange={(e) => {
                                    setEndLocation(e.target.value);
                                    handleSearchLocation(e.target.value, false);
                                }}
                                placeholder="Point d'arrivée"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box',
                                }}
                            />
                            {endSuggestions.length > 0 && (
                                <div
                                    style={{
                                        backgroundColor: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        marginTop: '5px',
                                        maxHeight: '150px',
                                        overflow: 'auto',
                                    }}
                                >
                                    {endSuggestions.map((s, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => selectSuggestion(s, false)}
                                            style={{
                                                padding: '10px',
                                                cursor: 'pointer',
                                                borderBottom: idx < endSuggestions.length - 1 ? '1px solid #eee' : 'none',
                                                fontSize: '12px',
                                            }}
                                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                                            onMouseLeave={(e) => (e.target.style.backgroundColor = 'white')}
                                        >
                                            {s.display_name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Checkbox itinéraire alternatif */}
                        <div style={{ marginBottom: '15px' }}>
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '10px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={useSurfacePreference}
                                    onChange={(e) => setUseSurfacePreference(e.target.checked)}
                                    style={{ marginRight: '8px' }}
                                />
                                Itinéraire alternatif personnalisé
                            </label>
                        </div>

                        {/* Préférences de surface */}
                        {useSurfacePreference && (
                            <div style={{ marginBottom: '15px' }}>
                                {['asphalt', 'gravel', 'dirt', 'grass', 'paved'].map((surface) => {
                                    const labels = {
                                        asphalt: 'Asphalte',
                                        gravel: 'Gravier',
                                        dirt: 'Terre',
                                        grass: 'Herbe',
                                        paved: 'Pavé',
                                    };

                                    return (
                                        <div key={surface} style={{ marginBottom: '10px' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    marginBottom: '5px',
                                                    fontSize: '12px',
                                                }}
                                            >
                        <span>
                          {SURFACE_ICONS[surface]} {labels[surface]}
                        </span>
                                                <span style={{ fontWeight: 'bold', color: SURFACE_COLORS[surface] }}>
                          {tempSurfacePreferences[surface]}%
                        </span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={tempSurfacePreferences[surface]}
                                                onChange={(e) => updateTempSurfacePreference(surface, parseInt(e.target.value))}
                                                style={{
                                                    width: '100%',
                                                    height: '4px',
                                                    borderRadius: '2px',
                                                    background: `linear-gradient(to right, ${SURFACE_COLORS[surface]} 0%, ${SURFACE_COLORS[surface]} ${tempSurfacePreferences[surface]}%, #e0e0e0 ${tempSurfacePreferences[surface]}%, #e0e0e0 100%)`,
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                    <button
                                        onClick={normalizeSurfacePreferences}
                                        style={{
                                            flex: 1,
                                            padding: '10px',
                                            backgroundColor: '#4caf50',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        ✓ Valider
                                    </button>
                                    <button
                                        onClick={resetSurfacePreferences}
                                        style={{
                                            padding: '10px',
                                            backgroundColor: '#ff9800',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                        }}
                                    >
                                        ↺
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Boutons d'action */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                            <button
                                onClick={analyzeRoute}
                                disabled={loading || points.length < 2}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    backgroundColor: loading ? '#ccc' : '#2196f3',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                }}
                            >
                                {loading ? 'Analyse...' : '📊 Analyser'}
                            </button>
                            <button
                                onClick={clearRoute}
                                style={{
                                    padding: '12px 20px',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                🗑️
                            </button>
                        </div>

                        {/* Message d'erreur */}
                        {error && (
                            <div
                                style={{
                                    padding: '10px',
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                }}
                            >
                                {error}
                            </div>
                        )}

                        {/* Aide pour les marqueurs */}
                        {points.length > 0 && (
                            <div
                                style={{
                                    marginTop: '15px',
                                    padding: '10px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '8px',
                                    fontSize: '11px',
                                    color: '#1976d2',
                                }}
                            >
                                💡 <strong>Astuce :</strong> Glisse-dépose les marqueurs sur la carte pour ajuster ton parcours en temps réel !
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Bouton pour réafficher le panneau gauche */}
            {!showLeftPanel && (
                <button
                    onClick={() => setShowLeftPanel(true)}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        zIndex: 1000,
                        padding: '12px 15px',
                        backgroundColor: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        fontSize: '18px',
                    }}
                >
                    ☰
                </button>
            )}

            {/* Panneau de résultats avec les gestionnaires de survol */}
            {showRightPanel && (routeData || alternativeRouteData) && (
                <ResultsPanel
                    routeData={routeData}
                    alternativeRouteData={alternativeRouteData}
                    onClose={() => setShowRightPanel(false)}
                    onStandardRouteHover={setIsStandardRouteHovered}
                    onAlternativeRouteHover={setIsAlternativeRouteHovered}
                />
            )}
        </div>
    );
}

export default GravelRouteTracker;